import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { useThemeStore } from '../store/themeStore';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { RichTextarea } from '../components/RichTextarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { Bot, Sun, Moon, User, Bell, Shield, Building2, Loader2, FileText, X, Image, Key, Mail, Pencil, Check, AlertTriangle, Trash2, Globe, Hash, RefreshCw, CheckCircle2, Settings2, Clock, FileSpreadsheet, CreditCard, Headset, Coins } from 'lucide-react';
import McpTab from '../components/settings/McpTab';
import { toast } from 'sonner';
import { formatABN, cn } from '../lib/utils';
import { formatPrice, getRegionConfig, getAvailableRegions, getTaxName, getPhonePlaceholder, hasBusinessStructure, hasCrewSuper } from '../lib/regionConfig';
import { PHASE_TYPES, DEFAULT_PHASE_RATES } from '../lib/phaseRates';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { signInProviderLabel } from '../lib/signInProviders';
import { PlanTierCard, PlanTierRow, FOUNDING_PROMISE, FOUNDING_FEATURES, comingTiers } from '../components/PlanTierCard';
import { PageToolbar } from '../components/PageToolbar';
import { PhonePanel } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';
import FileDropzone from '@/components/FileDropzone';

// Buttons that sit on the Billing tab's inverse Founding plan card. The
// primitives' primary and outline variants are drawn for a light surface, so on
// the inverse band they swap to its own foreground.
const ON_INVERSE_PRIMARY = 'bg-inverse-foreground text-inverse hover:bg-inverse-foreground/90';
const ON_INVERSE_OUTLINE = 'border-inverse-foreground/40 bg-transparent text-inverse-foreground hover:bg-inverse-foreground/10 hover:text-inverse-foreground';
// A status heading inside that panel: a soft pill keeps its own contrast on the band.
const STATUS_PILL = 'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-sm font-medium';

// The tabs as the phone panel lists them (desktop's TabsList shows icons too)
const SETTINGS_TABS = [
  { value: 'business', label: 'Business' },
  { value: 'estimates', label: 'Estimates' },
  { value: 'billing', label: 'Billing' },
  { value: 'account', label: 'Account' },
  { value: 'xero', label: 'Xero' },
  { value: 'mcp', label: 'MCP' },
  { value: 'other', label: 'Other' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateProfile, changePassword, getAccountType, deleteOwnAccount, subscription, checkSubscriptionStatus, getSubscriptionPlans, createCheckoutSession, createOnboardingCheckout, checkCheckoutStatus, cancelSubscription, uncancelSubscription, openBillingPortal, resubscribe, hasProjectsPlan, isAdmin, checkProjectsPlanAccess } = useAuthStore();
  const { settings, fetchSettings, updateSettings, getJobNumberPreview, renumberJobs, getXeroStatus, getXeroAuthUrl, disconnectXero, getXeroAccounts, getXeroTaxRates, getXeroAccountMappings, updateXeroAccountMappings } = useAppStore();
  const { theme, setTheme } = useThemeStore();
  const [initialLoading, setInitialLoading] = useState(true);
  const fileInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Auto-save status
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  // Active tab - default to URL param or 'business'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'business');
  const isPhone = useIsPhone();

  // Account section state
  const [accountType, setAccountType] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Delete account state
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Job numbering state
  const [jobNumberPreview, setJobNumberPreview] = useState(null);
  const [renumberDialogOpen, setRenumberDialogOpen] = useState(false);
  const [renumbering, setRenumbering] = useState(false);

  // Xero integration state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: null });
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [xeroAccountMappings, setXeroAccountMappings] = useState({
    labour_account: '200',
    equipment_account: '200',
    expenses_account: '200',
    crew_labour_account: '200',
    crew_equipment_account: '200',
    crew_super_account: '200',
    crew_po_labour_account: '300',
    crew_po_equipment_account: '300',
    crew_po_labour_tax_type: '',
    crew_po_equipment_tax_type: '',
    crew_super_bill_account: '300',
    crew_super_bill_tax_type: ''
  });
  const [loadingXeroAccounts, setLoadingXeroAccounts] = useState(false);
  const [xeroDataLoaded, setXeroDataLoaded] = useState(false); // Track if Xero accounts have been loaded
  const [xeroTaxRates, setXeroTaxRates] = useState([]);

  // Billing state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(false);
  // Plan and add-on prices, from the backend's plans.py via /subscription/plans
  const [planInfo, setPlanInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getSubscriptionPlans().then((r) => { if (!cancelled && r.success) setPlanInfo(r.data); });
    return () => { cancelled = true; };
  }, [getSubscriptionPlans]);
  const founding = planInfo?.plans?.founding_member;
  // Founding is the early price of the Regular plan, so Regular's price is struck through beside it
  const regular = planInfo?.plans?.regular;
  const onboarding = planInfo?.onboarding;
  const currencyLabel = (c) => String(c || '').toUpperCase();
  // A plan card's price, or null (a dash) until the plans have loaded
  const monthlyPrice = (plan, was = null) => (plan ? {
    was: was && was.price > plan.price ? formatPrice(was.price, was.currency) : null,
    amount: formatPrice(plan.price, plan.currency),
    period: `/ month ${currencyLabel(plan.currency)}`,
  } : null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [addOnboarding, setAddOnboarding] = useState(false);

  const [formData, setFormData] = useState({
    // Region
    region: 'AU',
    // Freelancer type
    freelancer_type: 'sole_trader',
    // Business identity
    company_name: '',
    business_name: '',
    registered_name: '',
    abn: '',
    business_number: '',
    // Logo
    business_logo: '',
    // Contact
    business_address: '',
    business_phone: '',
    business_email: '',
    // GST/VAT/Sales Tax
    collects_gst: false,
    gst_rate: 10,
    // Payment terms
    payment_terms: 'Payment due within 14 days of invoice date',
    terms_conditions: '',
    description_template: '',
    crew_po_footer: '',
    // Rate defaults
    default_base_hours: 10,
    default_overtime_multiplier: 1.5,
    default_double_time_threshold: 15,
    default_phase_rates: { ...DEFAULT_PHASE_RATES },
    // Notifications
    notifications_enabled: true,
    email_updates_enabled: false,
    // Job numbering
    job_number_prefix: '',
    job_number_start: 1,
    job_number_enabled: false,
  });

  // Track if form has been initialized to prevent auto-save on load
  const [formInitialized, setFormInitialized] = useState(false);
  
  // Polar checkout - simple redirect-based flow
  // No SDK initialization needed - Polar handles everything via hosted checkout

  // Open Polar checkout
  const openPolarCheckout = async (plan) => {
    setCheckoutLoading(true);
    
    try {
      // Get checkout URL from backend
      const result = await createCheckoutSession(plan);
      
      if (!result.success) {
        toast.error(result.error || 'Failed to start checkout');
        setCheckoutLoading(false);
        return;
      }
      
      // Redirect to Polar checkout page
      if (result.data.checkout_url) {
        window.location.href = result.data.checkout_url;
      } else {
        toast.error('No checkout URL received');
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to open checkout');
      setCheckoutLoading(false);
    }
  };

  // Open Polar checkout for the one-time onboarding service
  const openOnboardingCheckout = async () => {
    setOnboardingLoading(true);
    try {
      const result = await createOnboardingCheckout();
      if (!result.success) {
        toast.error(result.error || 'Failed to start onboarding checkout');
        setOnboardingLoading(false);
        return;
      }
      if (result.data.checkout_url) {
        window.location.href = result.data.checkout_url;
      } else {
        toast.error('No checkout URL received');
        setOnboardingLoading(false);
      }
    } catch (error) {
      console.error('Onboarding checkout error:', error);
      toast.error('Failed to open onboarding checkout');
      setOnboardingLoading(false);
    }
  };

  // Auto-save function
  const autoSave = useCallback(async (dataToSave) => {
    if (!formInitialized) return;
    
    setSaveStatus('saving');
    try {
      await updateSettings({ ...dataToSave });
      setSaveStatus('saved');
      
      // Reset to idle after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('idle');
      toast.error('Failed to save settings');
    }
  }, [updateSettings, formInitialized]);

  // Debounced auto-save when formData changes
  useEffect(() => {
    if (!formInitialized) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(formData);
    }, 800); // 800ms debounce
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, autoSave, formInitialized]);

  // Load settings - all API calls in parallel for faster page load
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Make all API calls in parallel instead of sequential
        // Use Promise.allSettled to handle partial failures gracefully
        const [settingsResult, accountResult, xeroResult] = await Promise.allSettled([
          fetchSettings(),
          getAccountType(),
          getXeroStatus()
        ]);
        
        // Handle settings data
        const data = settingsResult.status === 'fulfilled' ? settingsResult.value : null;
        if (data) {
          setFormData({
            region: data.region || 'AU',
            freelancer_type: data.freelancer_type || 'sole_trader',
            company_name: data.company_name || '',
            business_name: data.business_name || '',
            registered_name: data.registered_name || '',
            abn: data.abn || '',
            business_number: data.business_number || '',
            business_logo: data.business_logo || '',
            business_address: data.business_address || '',
            business_phone: data.business_phone || '',
            business_email: data.business_email || '',
            collects_gst: data.collects_gst || false,
            gst_rate: data.gst_rate || 10,
            payment_terms: data.payment_terms || 'Payment due within 14 days of invoice date',
            terms_conditions: data.terms_conditions || '',
            description_template: data.description_template || '',
            crew_po_footer: data.crew_po_footer || '',
            default_base_hours: data.default_base_hours || 10,
            default_overtime_multiplier: data.default_overtime_multiplier || 1.5,
            default_double_time_threshold: data.default_double_time_threshold || 15,
            default_phase_rates: { ...DEFAULT_PHASE_RATES, ...(data.default_phase_rates || {}) },
            notifications_enabled: data.notifications_enabled ?? true,
            email_updates_enabled: data.email_updates_enabled || false,
            job_number_prefix: data.job_number_prefix || '',
            job_number_start: data.job_number_start || 1,
            job_number_enabled: data.job_number_enabled || false,
          });
        }
        
        // Handle account type
        const accountData = accountResult.status === 'fulfilled' ? accountResult.value : null;
        if (accountData?.success) {
          setAccountType(accountData.data);
        }
        
        // Handle Xero status
        const xeroStatusData = xeroResult.status === 'fulfilled' ? xeroResult.value : null;
        setXeroStatus(xeroStatusData || { connected: false, tenant_name: null });
      } catch (err) {
        console.error('Error loading settings:', err);
      } finally {
        setInitialLoading(false);
        // Enable auto-save after initial load
        setTimeout(() => setFormInitialized(true), 100);
      }
    };
    loadSettings();
  }, [fetchSettings, getAccountType, getXeroStatus]);

  // Fetch job number preview
  useEffect(() => {
    const fetchPreview = async () => {
      const preview = await getJobNumberPreview();
      if (preview) {
        setJobNumberPreview(preview);
      }
    };
    if (formInitialized) {
      fetchPreview();
    }
  }, [getJobNumberPreview, formData.job_number_enabled, formData.job_number_prefix, formData.job_number_start, formInitialized]);

  // Lazy load Xero accounts when landing on Xero tab directly or when Xero status changes
  useEffect(() => {
    const loadXeroData = async () => {
      if (activeTab === 'xero' && xeroStatus.connected && !xeroDataLoaded && !loadingXeroAccounts) {
        setLoadingXeroAccounts(true);
        try {
          const [accountsData, mappingsData, taxRatesData] = await Promise.all([
            getXeroAccounts(),
            getXeroAccountMappings(),
            getXeroTaxRates()
          ]);
          setXeroAccounts(accountsData.accounts || []);
          setXeroAccountMappings(mappingsData);
          setXeroTaxRates(taxRatesData.tax_rates || []);
          setXeroDataLoaded(true);
        } catch (err) {
          console.error('Error loading Xero data:', err);
        } finally {
          setLoadingXeroAccounts(false);
        }
      }
    };
    
    // Only run after initial loading is complete
    if (!initialLoading) {
      loadXeroData();
    }
  }, [activeTab, xeroStatus.connected, xeroDataLoaded, initialLoading, loadingXeroAccounts, getXeroAccounts, getXeroAccountMappings, getXeroTaxRates]);

  // Check for checkout session status when returning from Polar checkout
  useEffect(() => {
    const checkoutId = searchParams.get('checkout_id');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    const success = searchParams.get('success');
    const onboarding = searchParams.get('onboarding');
    
    // Handle Polar success redirect
    if (success === 'true' && activeTab === 'billing') {
      if (onboarding === 'true') {
        // A checkout begun before onboarding returned to Help: send it there,
        // where the payment is confirmed before anything says it's booked
        navigate('/help?onboarding=paid', { replace: true });
        return;
      }
      toast.success('Payment successful! Your subscription is now active.');
      checkSubscriptionStatus(true);
      // Sequential flow: if the user opted to also add onboarding, start that checkout now
      if (localStorage.getItem('nhd_pending_onboarding') === 'true') {
        localStorage.removeItem('nhd_pending_onboarding');
        setSearchParams({ tab: 'billing' });
        setTimeout(() => { openOnboardingCheckout(); }, 1200);
        return;
      }
      setSearchParams({ tab: 'billing' });
      return;
    }
    
    // Legacy: Handle session_id/checkout_id polling (for any remaining flows)
    const pollingId = checkoutId || sessionId;
    if (pollingId && activeTab === 'billing') {
      // Poll for payment status
      const pollPaymentStatus = async (attempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;
        
        if (attempts >= maxAttempts) {
          toast.error('Payment status check timed out. Please check your email for confirmation.');
          setPollingStatus(false);
          return;
        }
        
        setPollingStatus(true);
        
        try {
          const result = await checkCheckoutStatus(sessionId);
          
          if (result.success) {
            if (result.data.payment_status === 'paid') {
              toast.success('Payment successful! Thank you for subscribing.');
              // Refresh subscription status
              await checkSubscriptionStatus(true);
              // Clear the session_id from URL
              setSearchParams({ tab: 'billing' });
              setPollingStatus(false);
              return;
            } else if (result.data.status === 'expired') {
              toast.error('Payment session expired. Please try again.');
              setSearchParams({ tab: 'billing' });
              setPollingStatus(false);
              return;
            }
          }
          
          // Continue polling
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } catch (error) {
          console.error('Error checking payment status:', error);
          setPollingStatus(false);
        }
      };
      
      pollPaymentStatus();
    }
    
    if (canceled === 'true') {
      toast.info('Payment was canceled.');
      setSearchParams({ tab: 'billing' });
    }
  }, [searchParams, activeTab, checkCheckoutStatus, checkSubscriptionStatus, setSearchParams]);

  // Load subscription status when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing' && !initialLoading) {
      checkSubscriptionStatus();
    }
  }, [activeTab, initialLoading, checkSubscriptionStatus]);

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
    // Xero data loading is handled by the useEffect above
  };

  // Generate local preview
  const generateLocalPreview = (prefix, startNum) => {
    if (!prefix && !startNum) return null;
    const now = new Date();
    let result = prefix;
    result = result.replace('{YYYY}', now.getFullYear().toString());
    result = result.replace('{YY}', now.getFullYear().toString().slice(-2));
    return `${result}${String(startNum || 1).padStart(4, '0')}`;
  };

  // Account management functions
  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    if (newName.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    
    setSavingName(true);
    const result = await updateProfile({ name: newName.trim() });
    setSavingName(false);
    
    if (result.success) {
      toast.success('Name updated successfully');
      setEditingName(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleEmailChangeRequest = () => {
    if (!newEmail.trim()) {
      toast.error('Email cannot be empty');
      return;
    }
    if (!newEmail.includes('@') || !newEmail.split('@')[1]?.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error('New email is the same as current email');
      return;
    }
    setEmailConfirmOpen(true);
  };

  const handleConfirmEmailChange = async () => {
    setSavingEmail(true);
    const result = await updateProfile({ email: newEmail.trim().toLowerCase() });
    setSavingEmail(false);
    setEmailConfirmOpen(false);
    
    if (result.success) {
      toast.success('Email updated successfully');
      setEditingEmail(false);
      setNewEmail('');
    } else {
      toast.error(result.error);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setSavingPassword(true);
    setPasswordError('');
    
    const result = await changePassword(currentPassword, newPassword);
    setSavingPassword(false);
    
    if (result.success) {
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(result.error);
    }
  };

  const resetPasswordDialog = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }
    
    setDeletingAccount(true);
    const result = await deleteOwnAccount();
    setDeletingAccount(false);
    
    if (result.success) {
      toast.success('Your account has been permanently deleted');
      navigate('/');
    } else {
      toast.error(result.error || 'Failed to delete account');
    }
  };

  const resetDeleteDialog = () => {
    setDeleteConfirmText('');
  };

  const handleRenumberJobs = async () => {
    setRenumbering(true);
    const result = await renumberJobs();
    setRenumbering(false);
    setRenumberDialogOpen(false);
    
    if (result.success) {
      toast.success(result.data.message);
      const preview = await getJobNumberPreview();
      if (preview) {
        setJobNumberPreview(preview);
      }
    } else {
      toast.error(result.error || 'Failed to renumber jobs');
    }
  };

  // Handle logo upload
  const handleLogoUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData({ ...formData, business_logo: e.target.result });
      toast.success('Logo uploaded');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setFormData({ ...formData, business_logo: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showSuperSection = formData.freelancer_type === 'sole_trader' || formData.freelancer_type === 'payg';

  const getNameLabel = () => {
    switch (formData.freelancer_type) {
      case 'company_trust':
        return 'Company / Trust Name';
      case 'sole_trader':
        return 'Business Name (Trading As)';
      case 'payg':
        return 'Your Full Name';
      default:
        return 'Name';
    }
  };

  const getNamePlaceholder = () => {
    switch (formData.freelancer_type) {
      case 'company_trust':
        return 'e.g., Acme Productions Pty Ltd';
      case 'sole_trader':
        return 'e.g., Smith Camera Services';
      case 'payg':
        return 'e.g., John Smith';
      default:
        return '';
    }
  };

  const getNameValue = () => {
    switch (formData.freelancer_type) {
      case 'company_trust':
        return formData.company_name;
      case 'sole_trader':
        return formData.business_name;
      case 'payg':
        return formData.registered_name;
      default:
        return '';
    }
  };

  const setNameValue = (value) => {
    switch (formData.freelancer_type) {
      case 'company_trust':
        setFormData({ ...formData, company_name: value });
        break;
      case 'sole_trader':
        setFormData({ ...formData, business_name: value });
        break;
      case 'payg':
        setFormData({ ...formData, registered_name: value });
        break;
      default:
        break;
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Autosave: saving, then saved. Beside the tabs on desktop, under them on a phone
  const saveStatusIndicator = (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {saveStatus === 'saving' && (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Saving...</span>
        </>
      )}
      {saveStatus === 'saved' && (
        <>
          <CheckCircle2 className="w-4 h-4 text-moss" />
          <span className="text-moss">Saved</span>
        </>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="settings-page">
        {/* Header */}
        <PageHeader title="Settings" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {isPhone ? (
            <>
              <PhonePanel tabs={{ value: activeTab, onChange: handleTabChange, items: SETTINGS_TABS }} />
              {/* Only while there's something to say, or its empty row adds a gap */}
              {(saveStatus === 'saving' || saveStatus === 'saved') && saveStatusIndicator}
            </>
          ) : (
          <PageToolbar>
            <TabsList className="max-w-full overflow-x-auto justify-start [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsTrigger value="business" className="flex items-center gap-2">
                <Building2 className="w-4 h-4 hidden sm:block" />
                Business
              </TabsTrigger>
              <TabsTrigger value="estimates" className="flex items-center gap-2">
                <FileText className="w-4 h-4 hidden sm:block" />
                Estimates
              </TabsTrigger>
              <TabsTrigger value="billing" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 hidden sm:block" />
                Billing
              </TabsTrigger>
              <TabsTrigger value="account" className="flex items-center gap-2">
                <User className="w-4 h-4 hidden sm:block" />
                Account
              </TabsTrigger>
              <TabsTrigger value="xero" className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 hidden sm:block" />
                Xero
              </TabsTrigger>
              <TabsTrigger value="mcp" className="flex items-center gap-2">
                <Bot className="w-4 h-4 hidden sm:block" />
                MCP
              </TabsTrigger>
              <TabsTrigger value="other" className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 hidden sm:block" />
                Other
              </TabsTrigger>
            </TabsList>
            {saveStatusIndicator}
          </PageToolbar>
          )}

          {/* ==================== BUSINESS TAB ==================== */}
          <TabsContent value="business" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Business Structure - regions with business structures (AU) */}
                {hasBusinessStructure(formData.region) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <Building2 className="w-[18px] h-[18px] text-ink-muted" /> Business structure
                      </CardTitle>
                      <CardDescription>How you operate as a freelancer</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-3">
                        <Label>Freelancer type</Label>
                        <RadioGroup
                          value={formData.freelancer_type}
                          onValueChange={(value) => setFormData({ ...formData, freelancer_type: value })}
                          className="space-y-2"
                        >
                          <div className="flex items-center space-x-3 p-3 rounded-cards border border-line-strong hover:bg-mist">
                            <RadioGroupItem value="company_trust" id="company_trust" />
                            <div>
                              <Label htmlFor="company_trust" className="font-medium cursor-pointer">Company / Trust</Label>
                              <p className="text-sm text-muted-foreground">Operating through a registered company or trust structure</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 p-3 rounded-cards border border-line-strong hover:bg-mist">
                            <RadioGroupItem value="sole_trader" id="sole_trader" />
                            <div>
                              <Label htmlFor="sole_trader" className="font-medium cursor-pointer">Sole trader</Label>
                              <p className="text-sm text-muted-foreground">Operating as an individual with an ABN</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 p-3 rounded-cards border border-line-strong hover:bg-mist">
                            <RadioGroupItem value="payg" id="payg" />
                            <div>
                              <Label htmlFor="payg" className="font-medium cursor-pointer">PAYG</Label>
                              <p className="text-sm text-muted-foreground">Working as an employee (Pay As You Go)</p>
                            </div>
                          </div>
                        </RadioGroup>
                      </div>

                      <Separator />

                      <div className="space-y-2">
                        <Label htmlFor="business-name">{getNameLabel()}</Label>
                        <Input
                          id="business-name"
                          value={getNameValue()}
                          onChange={(e) => setNameValue(e.target.value)}
                          placeholder={getNamePlaceholder()}
                        />
                      </div>

                      {formData.freelancer_type !== 'payg' && (
                        <div className="space-y-2">
                          <Label htmlFor="abn">ABN (Australian Business Number)</Label>
                          <Input
                            id="abn"
                            value={formData.abn}
                            onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                            placeholder="XX XXX XXX XXX"
                            maxLength={14}
                          />
                          {formData.abn && (
                            <p className="text-sm text-muted-foreground">Formatted: {formatABN(formData.abn)}</p>
                          )}
                        </div>
                      )}

                      <div className="space-y-4">
                        <Label className="text-base">Contact details</Label>
                        <p className="text-sm text-muted-foreground -mt-2">These appear on your estimates</p>
                        
                        <div className="space-y-2">
                          <Label htmlFor="business-address">Business address</Label>
                          <Input
                            id="business-address"
                            value={formData.business_address}
                            onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                            placeholder="123 Main St, Sydney NSW 2000"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="business-phone">Phone</Label>
                            <Input
                              id="business-phone"
                              value={formData.business_phone}
                              onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                              placeholder={getPhonePlaceholder(formData.region)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="business-email">Business email</Label>
                            <Input
                              id="business-email"
                              type="email"
                              value={formData.business_email}
                              onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                              placeholder="invoices@yourbusiness.com"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Simplified Business Section - every other region */}
                {!hasBusinessStructure(formData.region) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3">
                        <Building2 className="w-[18px] h-[18px] text-ink-muted" /> Business details
                      </CardTitle>
                      <CardDescription>Your business information for estimates</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="business-name-simple">Business name</Label>
                        <Input
                          id="business-name-simple"
                          value={formData.business_name}
                          onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                          placeholder="Your business or trading name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="business-number">
                          {getRegionConfig(formData.region).businessNumber.label} (Optional)
                        </Label>
                        <Input
                          id="business-number"
                          value={formData.business_number}
                          onChange={(e) => setFormData({ ...formData, business_number: e.target.value })}
                          placeholder={getRegionConfig(formData.region).businessNumber.placeholder}
                        />
                      </div>

                      <div className="space-y-4">
                        <Label className="text-base">Contact details</Label>
                        
                        <div className="space-y-2">
                          <Label htmlFor="business-address-simple">Business address</Label>
                          <Input
                            id="business-address-simple"
                            value={formData.business_address}
                            onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                            placeholder="123 Street, City, State, Postcode"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="business-phone-simple">Phone</Label>
                            <Input
                              id="business-phone-simple"
                              type="tel"
                              value={formData.business_phone}
                              onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                              placeholder={getPhonePlaceholder(formData.region)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="business-email-simple">Business email</Label>
                            <Input
                              id="business-email-simple"
                              type="email"
                              value={formData.business_email}
                              onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                              placeholder="contact@yourbusiness.com"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column. When the columns stack (below lg) its cards join
                  the grid (contents), so Region can go first: it sets the
                  currency and tax, and which business fields show */}
              <div className="space-y-6 max-lg:contents">
                {/* Region & Currency */}
                <Card className="max-lg:order-first">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Globe className="w-[18px] h-[18px] text-ink-muted" /> Region
                    </CardTitle>
                    <CardDescription>Your location affects currency and tax</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Select 
                      value={formData.region} 
                      onValueChange={(value) => {
                        const regionConfig = getRegionConfig(value);
                        setFormData({ 
                          ...formData, 
                          region: value,
                          gst_rate: regionConfig.tax.defaultRate
                        });
                      }}
                    >
                      <SelectTrigger data-testid="region-select">
                        <SelectValue placeholder="Select your region" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableRegions().map(region => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-sm text-muted-foreground">
                      <p>Currency: {getRegionConfig(formData.region).currency.code} ({getRegionConfig(formData.region).currency.symbol})</p>
                      <p>Tax: {getTaxName(formData.region)}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Business Logo */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Image className="w-[18px] h-[18px] text-ink-muted" /> Logo
                    </CardTitle>
                    <CardDescription>Your logo will appear on estimates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {formData.business_logo ? (
                      <div className="space-y-2">
                        <div className="relative inline-block">
                          <img 
                            src={formData.business_logo} 
                            alt="Business Logo" 
                            className="h-16 max-w-full object-contain rounded-small border border-line p-1 bg-white"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-5 w-5"
                            onClick={removeLogo}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto p-0 text-xs"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Replace
                        </Button>
                      </div>
                    ) : (
                      <FileDropzone
                        compact
                        accept="image/png,image/jpeg,image/jpg"
                        onSelect={handleLogoUpload}
                        title="Click to upload"
                        file={null}
                      />
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => { handleLogoUpload(e.target.files?.[0]); e.target.value = ''; }}
                      className="hidden"
                    />
                  </CardContent>
                </Card>

                {/* GST/VAT Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Shield className="w-[18px] h-[18px] text-ink-muted" /> {getTaxName(formData.region)}
                    </CardTitle>
                    <CardDescription>Configure tax for your estimates</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="gst-switch" className="text-sm">Registered</Label>
                      <Switch
                        id="gst-switch"
                        checked={formData.collects_gst}
                        onCheckedChange={(checked) => setFormData({ ...formData, collects_gst: checked })}
                      />
                    </div>
                    {formData.collects_gst && (
                      <div className="space-y-1">
                        <Label htmlFor="gst_rate" className="text-xs">Rate (%)</Label>
                        <Input
                          id="gst_rate"
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={formData.gst_rate}
                          onChange={(e) => setFormData({ ...formData, gst_rate: parseFloat(e.target.value) || 10 })}
                          className="h-8"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ==================== ESTIMATES TAB ==================== */}
          <TabsContent value="estimates" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Job Numbering */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Hash className="w-[18px] h-[18px] text-ink-muted" /> Job numbering
                    </CardTitle>
                    <CardDescription>Customise how job numbers are generated</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Enable custom job numbers</p>
                        <p className="text-sm text-muted-foreground">Generate sequential job numbers automatically</p>
                      </div>
                      <Switch
                        checked={formData.job_number_enabled}
                        onCheckedChange={(checked) => setFormData({ ...formData, job_number_enabled: checked })}
                        data-testid="job-numbering-switch"
                      />
                    </div>
                    
                    {formData.job_number_enabled && (
                      <>
                        <Separator />
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="job-prefix">Prefix</Label>
                            <Input
                              id="job-prefix"
                              value={formData.job_number_prefix}
                              onChange={(e) => setFormData({ ...formData, job_number_prefix: e.target.value })}
                              placeholder="e.g., JOB-{YYYY}-"
                              data-testid="job-prefix-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              Use <code className="px-1.5 py-0.5 bg-mist rounded-small text-xs">{'{YYYY}'}</code> for year or <code className="px-1.5 py-0.5 bg-mist rounded-small text-xs">{'{YY}'}</code> for short year
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="job-start">Starting number</Label>
                            <Input
                              id="job-start"
                              type="number"
                              min="1"
                              value={formData.job_number_start}
                              onChange={(e) => setFormData({ ...formData, job_number_start: parseInt(e.target.value) || 1 })}
                              data-testid="job-start-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              Zero-padded to 4 digits (0001, 0002)
                            </p>
                          </div>
                        </div>
                        
                        <div className="p-4 bg-mist rounded-cards">
                          <p className="text-sm font-medium mb-1">Preview</p>
                          <p className="text-2xl font-light tracking-tight">
                            {generateLocalPreview(formData.job_number_prefix, formData.job_number_start) || 'Enter a prefix to see preview'}
                          </p>
                          {jobNumberPreview?.next_number && jobNumberPreview.next_number !== formData.job_number_start && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Current next number: {jobNumberPreview.next_number}
                            </p>
                          )}
                        </div>
                        
                        <Separator />
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Re-number existing jobs</p>
                            <p className="text-sm text-muted-foreground">
                              Apply numbering to all existing jobs (ordered by creation date)
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => setRenumberDialogOpen(true)}
                            data-testid="renumber-jobs-btn"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Re-number jobs
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Job Description Template */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <FileText className="w-[18px] h-[18px] text-ink-muted" /> Job description template
                    </CardTitle>
                    <CardDescription>Default description that pre-fills when creating new jobs</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RichTextarea
                      value={formData.description_template}
                      onChange={(html) => setFormData({ ...formData, description_template: html })}
                      placeholder="Enter your default job description template...

Example:
- Project scope and deliverables
- Special requirements or notes
- Equipment and crew information"
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground mt-2">This template will be used as the default description for new jobs.</p>
                  </CardContent>
                </Card>

                {/* Terms & Conditions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <FileText className="w-[18px] h-[18px] text-ink-muted" /> Terms & conditions
                    </CardTitle>
                    <CardDescription>Standard terms that appear on your estimates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RichTextarea
                      value={formData.terms_conditions}
                      onChange={(html) => setFormData({ ...formData, terms_conditions: html })}
                      placeholder="Enter your standard terms and conditions...

Example:
- 50% deposit required to confirm booking
- Cancellation within 48 hours subject to full fee
- Additional hours billed at standard rate"
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground mt-2">Use the toolbar to format text with headings, bold, lists, and columns.</p>
                  </CardContent>
                </Card>

                {/* Crew PO Footer */}
                {(isAdmin) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <FileText className="w-[18px] h-[18px] text-ink-muted" /> Crew PO footer
                    </CardTitle>
                    <CardDescription>Payment terms that appear on crew purchase orders</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RichTextarea
                      value={formData.crew_po_footer}
                      onChange={(html) => setFormData({ ...formData, crew_po_footer: html })}
                      placeholder="Enter payment terms for crew purchase orders...

Example:
- Payment due within 7 days
- Invoice via email to accounts@example.com
- Please include PO number on invoice"
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground mt-2">Use the toolbar to apply <strong>bold</strong> or <em>italic</em> formatting.</p>
                  </CardContent>
                </Card>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Default Rates: what a new phase of each type starts at */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Coins className="w-[18px] h-[18px] text-ink-muted" /> Default rates
                    </CardTitle>
                    <CardDescription>The day rate each new phase starts at. Changing one doesn&apos;t change jobs you&apos;ve already made.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {PHASE_TYPES.map(({ value, label }) => (
                        <PhaseRateField
                          key={value}
                          id={`phase-rate-${value}`}
                          label={label}
                          region={formData.region}
                          value={formData.default_phase_rates?.[value] ?? DEFAULT_PHASE_RATES[value]}
                          onChange={(rate) => setFormData((prev) => ({
                            ...prev,
                            default_phase_rates: { ...prev.default_phase_rates, [value]: rate },
                          }))}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Overtime Calculation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Clock className="w-[18px] h-[18px] text-ink-muted" /> Overtime calculation
                    </CardTitle>
                    <CardDescription>Default values for new jobs</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="base-hours">Base hours</Label>
                      <Input
                        id="base-hours"
                        type="number"
                        value={formData.default_base_hours}
                        onChange={(e) => setFormData({ ...formData, default_base_hours: parseInt(e.target.value) || 10 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ot-multiplier">OT Multiplier</Label>
                      <Input
                        id="ot-multiplier"
                        type="number"
                        step="0.1"
                        value={formData.default_overtime_multiplier}
                        onChange={(e) => setFormData({ ...formData, default_overtime_multiplier: parseFloat(e.target.value) || 1.5 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dt-threshold">2T Threshold (hrs)</Label>
                      <Input
                        id="dt-threshold"
                        type="number"
                        value={formData.default_double_time_threshold}
                        onChange={(e) => setFormData({ ...formData, default_double_time_threshold: parseInt(e.target.value) || 15 })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ==================== BILLING TAB ==================== */}
          <TabsContent value="billing" className="space-y-6">
            {/* Every plan in one row. Founding, the plan on sale, is the featured inverse card */}
            <PlanTierRow>
              <PlanTierCard
                featured
                name="Solo"
                promise={FOUNDING_PROMISE}
                features={FOUNDING_FEATURES}
                price={monthlyPrice(founding, regular)}
              >
                {subscription?.plan === 'founding_member' && subscription?.status === 'active' ? (
                  <Button disabled className={`w-full ${ON_INVERSE_OUTLINE}`} variant="outline">
                    <CheckCircle2 className="w-4 h-4" />
                    Current plan
                  </Button>
                ) : (
                  <>
                    {!subscription?.onboarding_purchased && (
                      <label
                        htmlFor="add-onboarding-checkbox"
                        className="flex items-start gap-3 p-4 rounded-cards border border-inverse-muted/40 hover:bg-inverse-foreground/10 cursor-pointer transition-colors"
                        data-testid="add-onboarding-label"
                      >
                        <Checkbox
                          id="add-onboarding-checkbox"
                          checked={addOnboarding}
                          onCheckedChange={(v) => setAddOnboarding(!!v)}
                          className="mt-0.5 border-inverse-muted data-[state=checked]:border-inverse-foreground data-[state=checked]:bg-inverse-foreground data-[state=checked]:text-inverse"
                          data-testid="add-onboarding-checkbox"
                        />
                        <span className="flex-1 min-w-0 text-sm leading-snug">
                          <span className="block font-medium">Add equipment onboarding support</span>
                          {onboarding && (
                            <span className="block text-inverse-muted mb-1">One-time {formatPrice(onboarding.price, onboarding.currency)} {currencyLabel(onboarding.currency)}</span>
                          )}
                          <span className="block text-inverse-muted">
                            We&apos;ll personally help you import and set up your equipment data. You&apos;ll complete this as a second quick payment after subscribing.
                          </span>
                        </span>
                        {/* At the far right, clear of the checkbox */}
                        <Headset className="w-[18px] h-[18px] shrink-0 text-inverse-muted" aria-hidden="true" />
                      </label>
                    )}
                    <Button 
                      className={`w-full ${ON_INVERSE_PRIMARY}`}
                      onClick={() => {
                        if (addOnboarding) {
                          localStorage.setItem('nhd_pending_onboarding', 'true');
                        }
                        openPolarCheckout('founding_member');
                      }}
                      disabled={checkoutLoading}
                      data-testid="subscribe-founding-member-button"
                    >
                      {checkoutLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Subscribe now
                        </>
                      )}
                    </Button>
                  </>
                )}
              </PlanTierCard>
              {comingTiers(formData.region).map(({ key, ...tier }) => (
                <PlanTierCard key={key} {...tier} price={monthlyPrice(planInfo?.plans?.[key])} comingSoon>
                  <Button variant="outline" className="w-full" disabled>Coming soon</Button>
                </PlanTierCard>
              ))}
            </PlanTierRow>

            {/* Subscription details and the onboarding add-on */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* Current Plan */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <CreditCard className="w-[18px] h-[18px] text-muted-foreground" /> Current plan
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">Your subscription status and billing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {pollingStatus ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-foreground mr-2" />
                      <span>Checking payment status...</span>
                    </div>
                  ) : subscription ? (
                    <>
                      {/* Trial Status */}
                      {subscription.is_trial && (
                        <div className="p-4 bg-mist rounded-cards">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={cn(STATUS_PILL, 'bg-ochre-soft text-ochre')}>
                              <Clock className="w-4 h-4" />
                              Free trial
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {subscription.days_remaining > 0
                              ? `${subscription.days_remaining} days remaining`
                              : 'Trial ends today'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Upgrade to continue using all features after your trial ends.
                          </p>
                        </div>
                      )}

                      {/* Demo Account Status */}
                      {subscription.plan === 'demo' && (
                        <div className="p-4 bg-mist rounded-cards">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={cn(STATUS_PILL, 'bg-ochre-soft text-ochre')}>
                              <CheckCircle2 className="w-4 h-4" />
                              Demo account
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            This is a demo account with full access to all features.
                          </p>
                        </div>
                      )}

                      {/* Active Subscription */}
                      {subscription.status === 'active' && subscription.plan !== 'free_trial' && subscription.plan !== 'demo' && (
                        <div className="p-4 rounded-cards bg-mist">
                          <div className="flex items-center gap-2 mb-2">
                            {subscription.cancel_at_period_end ? (
                              <span className={cn(STATUS_PILL, 'bg-ochre-soft text-ochre')}>
                                <Clock className="w-4 h-4" />
                                Subscription ending
                              </span>
                            ) : (
                              <span className={cn(STATUS_PILL, 'bg-moss-soft text-moss')}>
                                <CheckCircle2 className="w-4 h-4" />
                                {subscription.plan === 'founding_member' ? 'Solo' : 'Active subscription'}
                              </span>
                            )}
                          </div>

                          {subscription.cancel_at_period_end && subscription.current_period_end ? (
                            <p className="text-sm text-muted-foreground mb-4">
                              Your subscription will end on{' '}
                              <span className="font-medium">
                                {new Date(typeof subscription.current_period_end === 'number'
                                  ? subscription.current_period_end * 1000
                                  : subscription.current_period_end
                                ).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </span>
                              . You&apos;ll retain full access until then.
                            </p>
                          ) : subscription.current_period_end ? (
                            <p className="text-sm text-muted-foreground mb-4">
                              Next billing date:{' '}
                              <span className="font-medium">
                                {new Date(typeof subscription.current_period_end === 'number'
                                  ? subscription.current_period_end * 1000
                                  : subscription.current_period_end
                                ).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </span>
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground mb-4">
                              You have full access to all features.
                            </p>
                          )}

                          <div className="flex gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                // Open Polar customer portal - use redirect to avoid popup blocker
                                setPortalLoading(true);
                                const result = await openBillingPortal();
                                if (result.success && result.data?.url) {
                                  // Redirect current page to portal (avoids popup blocker)
                                  window.location.href = result.data.url;
                                } else {
                                  toast.error(result.error || 'Failed to open billing portal');
                                  setPortalLoading(false);
                                }
                              }}
                              disabled={portalLoading}
                            >
                              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                              Manage billing
                            </Button>
                            {subscription.cancel_at_period_end ? (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={async () => {
                                  setResubscribing(true);
                                  const result = await uncancelSubscription();
                                  if (result.success) {
                                    toast.success('Subscription reactivated! It will now renew automatically.');
                                  } else {
                                    toast.error(result.error || 'Failed to reactivate subscription');
                                  }
                                  setResubscribing(false);
                                }}
                                disabled={resubscribing}
                              >
                                {resubscribing ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Reactivating...
                                  </>
                                ) : (
                                  'Keep subscription'
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCancelDialogOpen(true)}
                              >
                                Cancel subscription
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Invoices are sent to your email automatically. For billing issues, contact <a href="mailto:crew@nohalfdays.com" className="underline">crew@nohalfdays.com</a>
                          </p>
                        </div>
                      )}

                      {/* Trial Expired */}
                      {subscription.status === 'expired' && (
                        <div className="p-4 bg-mist rounded-cards">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={cn(STATUS_PILL, 'bg-brick-soft text-brick')}>
                              <AlertTriangle className="w-4 h-4" />
                              Trial expired
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            Your free trial has ended.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            You can still view your data, but you&apos;ll need to upgrade to create new jobs and estimates.
                          </p>
                        </div>
                      )}

                      {/* Canceled Subscription */}
                      {subscription.status === 'canceled' && (
                        <div className="p-4 bg-mist rounded-cards">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={cn(STATUS_PILL, 'bg-ochre-soft text-ochre')}>
                              <AlertTriangle className="w-4 h-4" />
                              Subscription canceled
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">
                            Your subscription has ended. Subscribe again to continue using all features.
                          </p>
                          <Button
                            size="sm"
                            onClick={() => openPolarCheckout('founding_member')}
                            disabled={checkoutLoading}
                          >
                            {checkoutLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4" />
                                Subscribe now
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-muted-foreground mt-2">
                            You&apos;ll be redirected to complete payment
                          </p>
                        </div>
                      )}

                      {/* Full access with no subscription behind it (e.g. an admin account).
                          None of the states above matches, which used to leave this card blank */}
                      {!subscription.is_trial && subscription.plan === 'free_trial' && subscription.status === 'active' && (
                        <div className="p-4 bg-mist rounded-cards">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={cn(STATUS_PILL, 'bg-moss-soft text-moss')}>
                              <CheckCircle2 className="w-4 h-4" />
                              Full access
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            This account has every feature without a subscription.
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </CardContent>
              </Card>

            {/* Equipment Onboarding Support (one-time add-on) */}
            {subscription && subscription.plan !== 'demo' && (
              <Card data-testid="onboarding-support-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Headset className="w-[18px] h-[18px] text-ink-muted" /> Equipment onboarding support
                  </CardTitle>
                  <CardDescription>
                    Optional one-time service — we&apos;ll personally help you import and set up your equipment data.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {subscription.onboarding_purchased ? (
                    <div className="flex items-center gap-3 p-4 bg-moss-soft rounded-cards" data-testid="onboarding-active-banner">
                      <CheckCircle2 className="w-[18px] h-[18px] text-moss shrink-0" />
                      <div>
                        <p className="font-medium text-moss">Onboarding booked</p>
                        <p className="text-sm text-muted-foreground">
                          Send us your gear list and follow along in{' '}
                          <Link to="/help" className="text-clay underline underline-offset-4 hover:text-foreground">Help</Link>,
                          under your onboarding conversation.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground max-w-md">
                          Skip the manual data entry. Pay once and we&apos;ll get your gear list, rates and packages
                          set up for you.
                        </p>
                        {onboarding && (
                          <p className="mt-2 text-4xl font-light tracking-tight">
                            {formatPrice(onboarding.price, onboarding.currency)} <span className="text-xs font-normal text-muted-foreground">one-time {currencyLabel(onboarding.currency)}</span>
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={openOnboardingCheckout}
                        disabled={onboardingLoading}
                        data-testid="add-onboarding-button"
                      >
                        {onboardingLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Headset className="w-4 h-4" />
                            Add onboarding support
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </div>

          </TabsContent>

          {/* ==================== ACCOUNT TAB ==================== */}
          <TabsContent value="account" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <User className="w-[18px] h-[18px] text-ink-muted" /> Account
                  </CardTitle>
                  <CardDescription>Manage your login credentials and account details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Name Field */}
                <div className="space-y-2">
                  <Label>Display name</Label>
                  {editingName ? (
                    <div className="flex gap-2">
                      <Input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Enter your name"
                        className="flex-1"
                        autoFocus
                      />
                      <Button onClick={handleSaveName} disabled={savingName} size="sm">
                        {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setEditingName(false);
                          setNewName(user?.name || '');
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={user?.name || ''} disabled className="flex-1" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setNewName(user?.name || '');
                          setEditingName(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Email Field */}
                <div className="space-y-2">
                  <Label>Email address</Label>
                  {editingEmail ? (
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Enter new email"
                        className="flex-1"
                        autoFocus
                      />
                      <Button onClick={handleEmailChangeRequest} disabled={savingEmail} size="sm">
                        {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setEditingEmail(false);
                          setNewEmail('');
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={user?.email || ''} disabled className="flex-1" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setNewEmail(user?.email || '');
                          setEditingEmail(true);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {signInProviderLabel(accountType?.auth_type) && (
                    <p className="text-sm text-muted-foreground">
                      <Mail className="w-3 h-3 inline mr-1" />
                      Signed in with {signInProviderLabel(accountType?.auth_type)}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Password Change */}
                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {accountType?.can_change_password 
                        ? 'Change your account password'
                        : signInProviderLabel(accountType?.auth_type)
                          ? `Password managed by ${signInProviderLabel(accountType?.auth_type)}`
                          : 'Password not available'
                      }
                    </p>
                    {accountType?.can_change_password && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          resetPasswordDialog();
                          setPasswordDialogOpen(true);
                        }}
                      >
                        <Key className="w-4 h-4" />
                        Change password
                      </Button>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Delete Account */}
                <div className="space-y-2">
                  <Label className="text-destructive">Danger zone</Label>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Delete account</p>
                      <p className="text-xs text-muted-foreground">
                        Permanently delete your account and all associated data
                      </p>
                    </div>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => {
                        resetDeleteDialog();
                        setDeleteAccountDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete account
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* ==================== OTHER TAB ==================== */}
          <TabsContent value="other" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Appearance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    {theme === 'dark' ? <Moon className="w-[18px] h-[18px] text-ink-muted" /> : <Sun className="w-[18px] h-[18px] text-ink-muted" />}
                    Appearance
                  </CardTitle>
                  <CardDescription>Customise how No Half Days looks</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Theme</p>
                      <p className="text-sm text-muted-foreground">Switch between light and dark mode</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={theme === 'light' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTheme('light')}
                      >
                        <Sun className="w-4 h-4" /> Light
                      </Button>
                      <Button
                        variant={theme === 'dark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTheme('dark')}
                      >
                        <Moon className="w-4 h-4" /> Dark
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Bell className="w-[18px] h-[18px] text-ink-muted" /> Notifications
                  </CardTitle>
                  <CardDescription>Configure how you receive updates</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Push notifications</p>
                      <p className="text-sm text-muted-foreground">Receive browser notifications</p>
                    </div>
                    <Switch
                      checked={formData.notifications_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, notifications_enabled: checked })}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email updates</p>
                      <p className="text-sm text-muted-foreground">Receive weekly summary emails</p>
                    </div>
                    <Switch
                      checked={formData.email_updates_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, email_updates_enabled: checked })}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== MCP TAB ==================== */}
          <TabsContent value="mcp" className="space-y-6">
            <McpTab />
          </TabsContent>

          {/* ==================== XERO TAB ==================== */}
          <TabsContent value="xero" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Xero Integration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <FileSpreadsheet className="w-[18px] h-[18px] text-foreground" /> Xero integration
                  </CardTitle>
                  <CardDescription>Connect to Xero for direct invoice creation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {xeroStatus.connected ? (
                    <>
                      <div className="flex items-center gap-3 p-3 rounded-cards bg-moss-soft">
                        <CheckCircle2 className="w-[18px] h-[18px] text-moss shrink-0" />
                        <div>
                          <p className="font-medium text-moss">Connected to Xero</p>
                          <p className="text-sm text-muted-foreground">{xeroStatus.tenant_name}</p>
                        </div>
                      </div>
                      
                      {/* Account Mappings */}
                      <Separator />
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium">Account mappings</h4>
                        <p className="text-xs text-muted-foreground">Select which Xero accounts to use for each invoice category</p>
                        
                        {loadingXeroAccounts ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : xeroAccounts.length > 0 ? (
                          <div className="space-y-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Labour account</Label>
                              <Select
                                value={xeroAccountMappings.labour_account}
                                onValueChange={async (value) => {
                                  const newMappings = { ...xeroAccountMappings, labour_account: value };
                                  setXeroAccountMappings(newMappings);
                                  try {
                                    await updateXeroAccountMappings(newMappings);
                                    toast.success('Account mapping saved');
                                  } catch (err) {
                                    toast.error('Failed to save mapping');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                  {xeroAccounts
                                    .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                    .map(acc => (
                                      <SelectItem key={acc.code} value={acc.code}>
                                        {acc.code} - {acc.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1.5">
                              <Label className="text-xs">Equipment account</Label>
                              <Select
                                value={xeroAccountMappings.equipment_account}
                                onValueChange={async (value) => {
                                  const newMappings = { ...xeroAccountMappings, equipment_account: value };
                                  setXeroAccountMappings(newMappings);
                                  try {
                                    await updateXeroAccountMappings(newMappings);
                                    toast.success('Account mapping saved');
                                  } catch (err) {
                                    toast.error('Failed to save mapping');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                  {xeroAccounts
                                    .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                    .map(acc => (
                                      <SelectItem key={acc.code} value={acc.code}>
                                        {acc.code} - {acc.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1.5">
                              <Label className="text-xs">Expenses account</Label>
                              <Select
                                value={xeroAccountMappings.expenses_account}
                                onValueChange={async (value) => {
                                  const newMappings = { ...xeroAccountMappings, expenses_account: value };
                                  setXeroAccountMappings(newMappings);
                                  try {
                                    await updateXeroAccountMappings(newMappings);
                                    toast.success('Account mapping saved');
                                  } catch (err) {
                                    toast.error('Failed to save mapping');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                  {xeroAccounts
                                    .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                    .map(acc => (
                                      <SelectItem key={acc.code} value={acc.code}>
                                        {acc.code} - {acc.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Crew Account Mappings - gated for Projects plan */}
                            {(isAdmin) && (
                              <>
                                <Separator className="my-4" />
                                <p className="text-xs font-medium text-muted-foreground">Crew account mappings (income)</p>
                                
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Crew labour account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_labour_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_labour_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Crew equipment account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_equipment_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_equipment_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                {/* Crew super is Australian: no account to map anywhere else */}
                                {hasCrewSuper(formData.region) && (
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Crew superannuation account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_super_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_super_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['REVENUE', 'SALES', 'OTHERINCOME'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                )}
                              </>
                            )}

                            {/* Crew PO Account Mappings (Expense accounts for bills) */}
                            {(isAdmin) && (
                              <>
                                <Separator className="my-4" />
                                <p className="text-xs font-medium text-muted-foreground">Crew PO account mappings (expense)</p>
                                <p className="text-xs text-muted-foreground mb-2">Used when sending Crew POs to Xero as Draft Bills</p>
                                
                                <div className="space-y-1.5">
                                  <Label className="text-xs">PO labour account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_po_labour_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_po_labour_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['DIRECTCOSTS', 'EXPENSE', 'OVERHEADS'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1.5">
                                  <Label className="text-xs">PO labour tax rate</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_po_labour_tax_type || '_default'}
                                    onValueChange={async (value) => {
                                      const taxType = value === '_default' ? '' : value;
                                      const newMappings = { ...xeroAccountMappings, crew_po_labour_tax_type: taxType };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Tax rate mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Account default" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="_default">Account default</SelectItem>
                                      {xeroTaxRates
                                        .filter(tr => tr.can_apply_to_expenses)
                                        .map(tr => (
                                          <SelectItem key={tr.tax_type} value={tr.tax_type}>
                                            {tr.name} ({tr.effective_rate}%)
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div className="space-y-1.5 mt-3">
                                  <Label className="text-xs">PO equipment account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_po_equipment_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_po_equipment_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['DIRECTCOSTS', 'EXPENSE', 'OVERHEADS'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1.5">
                                  <Label className="text-xs">PO equipment tax rate</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_po_equipment_tax_type || '_default'}
                                    onValueChange={async (value) => {
                                      const taxType = value === '_default' ? '' : value;
                                      const newMappings = { ...xeroAccountMappings, crew_po_equipment_tax_type: taxType };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Tax rate mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Account default" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="_default">Account default</SelectItem>
                                      {xeroTaxRates
                                        .filter(tr => tr.can_apply_to_expenses)
                                        .map(tr => (
                                          <SelectItem key={tr.tax_type} value={tr.tax_type}>
                                            {tr.name} ({tr.effective_rate}%)
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}

                            {/* Superannuation Bill Mappings */}
                            {isAdmin && hasCrewSuper(formData.region) && (
                              <>
                                <Separator className="my-4" />
                                <p className="text-xs font-medium text-muted-foreground">Superannuation bill mappings (expense)</p>
                                <p className="text-xs text-muted-foreground mb-2">Used when sending Superannuation summary to Xero as a Draft Bill</p>
                                
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Super bill account</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_super_bill_account}
                                    onValueChange={async (value) => {
                                      const newMappings = { ...xeroAccountMappings, crew_super_bill_account: value };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Account mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select account" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {xeroAccounts
                                        .filter(acc => acc.code && ['DIRECTCOSTS', 'EXPENSE', 'OVERHEADS'].includes(acc.type))
                                        .map(acc => (
                                          <SelectItem key={acc.code} value={acc.code}>
                                            {acc.code} - {acc.name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1.5">
                                  <Label className="text-xs">Super bill tax rate</Label>
                                  <Select
                                    value={xeroAccountMappings.crew_super_bill_tax_type || '_default'}
                                    onValueChange={async (value) => {
                                      const taxType = value === '_default' ? '' : value;
                                      const newMappings = { ...xeroAccountMappings, crew_super_bill_tax_type: taxType };
                                      setXeroAccountMappings(newMappings);
                                      try {
                                        await updateXeroAccountMappings(newMappings);
                                        toast.success('Tax rate mapping saved');
                                      } catch (err) {
                                        toast.error('Failed to save mapping');
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Account default" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="_default">Account default</SelectItem>
                                      {xeroTaxRates
                                        .filter(tr => tr.can_apply_to_expenses)
                                        .map(tr => (
                                          <SelectItem key={tr.tax_type} value={tr.tax_type}>
                                            {tr.name} ({tr.effective_rate}%)
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              setLoadingXeroAccounts(true);
                              try {
                                const data = await getXeroAccounts();
                                setXeroAccounts(data.accounts || []);
                              } catch (err) {
                                toast.error('Failed to load Xero accounts');
                              } finally {
                                setLoadingXeroAccounts(false);
                              }
                            }}
                            disabled={loadingXeroAccounts}
                            className="w-full"
                          >
                            {loadingXeroAccounts ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Load Xero accounts
                          </Button>
                        )}
                      </div>
                      
                      <Separator />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          setXeroLoading(true);
                          try {
                            await disconnectXero();
                            setXeroStatus({ connected: false, tenant_name: null });
                            setXeroAccounts([]);
                            toast.success('Xero disconnected');
                          } catch (err) {
                            toast.error('Failed to disconnect Xero');
                          } finally {
                            setXeroLoading(false);
                          }
                        }}
                        disabled={xeroLoading}
                        className="w-full"
                      >
                        {xeroLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Disconnect Xero
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Connect your Xero account to send invoices directly from job pages.
                      </p>
                      <Button
                        onClick={async () => {
                          setXeroLoading(true);
                          try {
                            const { auth_url } = await getXeroAuthUrl();
                            window.location.href = auth_url;
                          } catch (err) {
                            toast.error('Failed to start Xero connection');
                            setXeroLoading(false);
                          }
                        }}
                        disabled={xeroLoading}
                        className="w-full"
                      >
                        {xeroLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                        Connect to Xero
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* ==================== DIALOGS ==================== */}
        
        {/* Re-number Jobs Confirmation Dialog */}
        <Dialog open={renumberDialogOpen} onOpenChange={setRenumberDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Re-number all jobs?</DialogTitle>
              <DialogDescription>
                This will assign new job numbers to all your existing jobs based on your current settings.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm">
                Jobs will be numbered in order of their creation date, starting from{' '}
                <strong>{generateLocalPreview(formData.job_number_prefix, formData.job_number_start)}</strong>.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenumberDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRenumberJobs} disabled={renumbering}>
                {renumbering ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Re-number jobs
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Change Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) resetPasswordDialog();
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change password</DialogTitle>
              <DialogDescription>
                Enter your current password and choose a new password.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Enter new password"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum 8 characters, must include at least one number
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Confirm new password"
                />
              </div>
              {passwordError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  {passwordError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleChangePassword} disabled={savingPassword}>
                {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Change password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Change Confirmation Dialog */}
        <Dialog open={emailConfirmOpen} onOpenChange={setEmailConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm email change</DialogTitle>
              <DialogDescription>
                Are you sure you want to change your email address?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm">
                Your email will be changed from <strong>{user?.email}</strong> to <strong>{newEmail}</strong>.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                You will use this new email to sign in.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailConfirmOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmEmailChange} disabled={savingEmail}>
                {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Confirm change
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Account Confirmation Dialog */}
        <Dialog open={deleteAccountDialogOpen} onOpenChange={(open) => {
          setDeleteAccountDialogOpen(open);
          if (!open) resetDeleteDialog();
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-3">
                <AlertTriangle className="w-[18px] h-[18px] shrink-0" />
                Delete account
              </DialogTitle>
              <DialogDescription>
                This action is permanent and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-4 bg-brick-soft rounded-cards">
                <p className="text-sm font-medium text-brick mb-2">
                  The following data will be permanently deleted:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Your account and login credentials</li>
                  <li>All jobs you have created</li>
                  <li>All clients you have added</li>
                  <li>All equipment in your inventory</li>
                  <li>All day sheets and time tracking data</li>
                  <li>All your settings and preferences</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="delete-confirm">
                  Type <span className="font-medium text-destructive">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteAccountDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteAccount} 
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
              >
                {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete my account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel subscription?</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your subscription?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm">
                Your subscription will remain active until the end of your current billing period.
                You can resubscribe at any time.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                We'd love to know why you're leaving - reply to any of our emails with your feedback!
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Keep subscription
              </Button>
              <Button 
                variant="destructive" 
                onClick={async () => {
                  const result = await cancelSubscription();
                  if (result.success) {
                    toast.success('Subscription canceled. You\'ll retain access until the end of your billing period.');
                    setCancelDialogOpen(false);
                    checkSubscriptionStatus(true);
                  } else {
                    toast.error(result.error || 'Failed to cancel subscription');
                  }
                }}
              >
                Cancel subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bottom spacing */}
        <div className="pb-8" />
      </div>
    </Layout>
  );
}

/**
 * One default phase rate: a day rate with the region's currency symbol on
 * whichever side the region writes it. 0 is a real rate, so an empty field
 * saves as 0 rather than falling back to a default.
 */
const PhaseRateField = ({ id, label, region, value, onChange }) => {
  const { symbol, position } = getRegionConfig(region).currency;
  const after = position === 'after';
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span
          aria-hidden="true"
          className={cn('absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none', after ? 'right-3' : 'left-3')}
        >
          {symbol}
        </span>
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          className={cn('no-spinner tabular-nums', after ? 'pr-8' : 'pl-8')}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          data-testid={id}
        />
      </div>
    </div>
  );
};
